import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import managedAxios from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import {
  FileText, ArrowLeft, Loader2, Sparkles, BookOpen,
  CheckCircle, AlertCircle, Lightbulb, Target, Copy,
  ChevronDown, ChevronUp, Newspaper, PenTool
} from "lucide-react";

/**
 * TGPSC Editorial Analyzer - Analyze editorials for TGPSC preparation
 * Reuses UPSC Editorial Analyzer with TGPSC-specific analysis
 */

const TGPSCEditorialAnalyzerPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [editorialText, setEditorialText] = useState("");
  const [editorialUrl, setEditorialUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    keyPoints: true,
    tgpscRelevance: true,
    questions: true
  });

  const isPro = user?.role === "PRO";

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleAnalyze = async () => {
    if (!editorialText.trim() && !editorialUrl.trim()) {
      toast({
        variant: "destructive",
        description: "Please enter editorial text or URL"
      });
      return;
    }

    setLoading(true);
    setAnalysis(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.post(
        `${API_BASE}/editorial/analyze`,
        {
          text: editorialText,
          url: editorialUrl,
          exam_type: "TGPSC"
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000
        }
      );

      setAnalysis(response.data);
      toast({
        description: "Editorial analyzed successfully!"
      });
    } catch (err) {
      console.error("Error analyzing editorial:", err);
      
      // Demo analysis for testing
      setAnalysis({
        title: "Sample Editorial Analysis",
        source: "The Hindu",
        summary: "This editorial discusses the importance of state-specific governance reforms and their impact on public service delivery in Telangana. It highlights the role of decentralization and technology adoption in improving administrative efficiency.",
        key_points: [
          "State-level governance reforms are crucial for development",
          "Technology adoption in public services",
          "Decentralization of administrative functions",
          "Focus on welfare scheme implementation"
        ],
        tgpsc_relevance: {
          papers: ["Paper-II (Polity & Governance)", "Paper-VI (Telangana)"],
          topics: ["Governance", "Public Administration", "State Reforms"],
          importance: "high"
        },
        potential_questions: [
          {
            question: "Discuss the role of decentralization in improving governance at the state level with special reference to Telangana.",
            marks: 15,
            paper: "Paper-II"
          },
          {
            question: "How has technology adoption improved public service delivery in Telangana? Illustrate with examples.",
            marks: 10,
            paper: "Paper-VI"
          }
        ],
        keywords: ["Governance", "Decentralization", "Public Administration", "Telangana", "Reforms"]
      });
      
      toast({
        description: "Showing demo analysis. API integration in progress."
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ description: "Copied to clipboard!" });
  };

  const SectionHeader = ({ title, icon: Icon, section, badge }) => (
    <button
      onClick={() => toggleSection(section)}
      className="flex items-center justify-between w-full p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
    >
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-pink-600" />
        <span className="font-semibold text-slate-800">{title}</span>
        {badge && <Badge className="bg-pink-500 text-white text-xs">{badge}</Badge>}
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-5 h-5 text-slate-500" />
      ) : (
        <ChevronDown className="w-5 h-5 text-slate-500" />
      )}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/30 to-blue-50/30 pb-20">
      {/* Back Navigation */}
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/tgpsc")}
          className="text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          TGPSC Dashboard
        </Button>
      </div>

      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 pt-6 pb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
              Editorial Analyzer
            </h1>
            <p className="text-slate-600">AI-powered analysis for TGPSC preparation</p>
          </div>
          <Badge className="bg-pink-500 ml-auto">TGPSC</Badge>
        </div>

        {/* Info Card */}
        <Card className="bg-cyan-50 border-cyan-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-cyan-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-cyan-900">
                  Analyze Hindu/Indian Express editorials for TGPSC Mains
                </p>
                <p className="text-sm text-cyan-700 mt-1">
                  Get summary, key points, TGPSC paper relevance, and potential questions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Input Section */}
      <div className="max-w-4xl mx-auto px-4 mb-6">
        <Card>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className="text-slate-700">Paste Editorial Text</Label>
              <Textarea
                placeholder="Paste the editorial text here..."
                value={editorialText}
                onChange={(e) => setEditorialText(e.target.value)}
                rows={8}
                className="mt-2"
              />
            </div>

            <div className="text-center text-slate-400 text-sm">— OR —</div>

            <div>
              <Label className="text-slate-700">Enter Editorial URL</Label>
              <input
                type="url"
                placeholder="https://www.thehindu.com/opinion/editorial/..."
                value={editorialUrl}
                onChange={(e) => setEditorialUrl(e.target.value)}
                className="w-full mt-2 px-3 py-2 border rounded-md"
              />
            </div>

            <Button
              onClick={handleAnalyze}
              disabled={loading || (!editorialText.trim() && !editorialUrl.trim())}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing for TGPSC...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Analyze Editorial
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Analysis Results */}
      {analysis && (
        <div className="max-w-4xl mx-auto px-4 space-y-4">
          {/* Summary Section */}
          <Card>
            <CardContent className="p-0">
              <SectionHeader 
                title="Summary" 
                icon={BookOpen} 
                section="summary" 
              />
              {expandedSections.summary && (
                <div className="p-4 border-t">
                  <p className="text-slate-700">{analysis.summary}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(analysis.summary)}
                    className="mt-2"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Key Points Section */}
          <Card>
            <CardContent className="p-0">
              <SectionHeader 
                title="Key Points" 
                icon={CheckCircle} 
                section="keyPoints"
                badge={`${analysis.key_points?.length || 0} points`}
              />
              {expandedSections.keyPoints && (
                <div className="p-4 border-t">
                  <ul className="space-y-2">
                    {analysis.key_points?.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500 mt-1 shrink-0" />
                        <span className="text-slate-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* TGPSC Relevance Section */}
          <Card>
            <CardContent className="p-0">
              <SectionHeader 
                title="TGPSC Relevance" 
                icon={Target} 
                section="tgpscRelevance"
                badge={analysis.tgpsc_relevance?.importance?.toUpperCase()}
              />
              {expandedSections.tgpscRelevance && (
                <div className="p-4 border-t space-y-4">
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-2">Relevant Papers:</p>
                    <div className="flex flex-wrap gap-2">
                      {analysis.tgpsc_relevance?.papers?.map((paper, idx) => (
                        <Badge key={idx} className="bg-pink-500 text-white">
                          {paper}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-2">Topics:</p>
                    <div className="flex flex-wrap gap-2">
                      {analysis.tgpsc_relevance?.topics?.map((topic, idx) => (
                        <Badge key={idx} variant="outline">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-2">Keywords:</p>
                    <div className="flex flex-wrap gap-2">
                      {analysis.keywords?.map((keyword, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-slate-100">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Potential Questions Section */}
          <Card>
            <CardContent className="p-0">
              <SectionHeader 
                title="Potential TGPSC Questions" 
                icon={PenTool} 
                section="questions"
                badge={`${analysis.potential_questions?.length || 0} questions`}
              />
              {expandedSections.questions && (
                <div className="p-4 border-t space-y-4">
                  {analysis.potential_questions?.map((q, idx) => (
                    <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-medium text-slate-800 mb-2">{q.question}</p>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{q.paper}</Badge>
                            <Badge variant="outline">{q.marks} marks</Badge>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(q.question)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tips */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Pro Tip</p>
                  <p className="text-sm text-amber-800 mt-1">
                    Use these potential questions to practice answer writing. 
                    Focus on Telangana-specific angles when the topic is relevant.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TGPSCEditorialAnalyzerPage;
